import { Pipe, PipeTransform } from '@angular/core';

import { PerdiodsEnum, TransactionTypeEnumV2 } from '../enums/wallet.enum';
import { IPointing } from '../interfaces/garderie.interface';
import { IMessage, IMessagingContact, IOrderedMessage } from '../interfaces/messaging.interface';
import { IUser } from '../interfaces/users/user.interface';
import { isDateInThisMonth, isDateInThisWeekButNotToday, isDateInThisYear, isToday } from '../utils/date.utils';
import { CreditTypeEnumV2, DebitTypeEnumV2 } from './../enums/wallet.enum';
import { ITransactionV2, IWalletFilter } from './../interfaces/wallet.interface';

@Pipe({
    name: 'searchMessage',
})
export class SearchMessagePipe implements PipeTransform {
    public transform(messages: IOrderedMessage[], searchText: string): any[] {
        if (!messages) {
            return [];
        }
        if (!searchText) {
            return messages;
        }
        searchText = searchText.toLowerCase();

        return messages.filter(
            (orderedMessage: IOrderedMessage) =>
                orderedMessage.message.message.toLowerCase().includes(searchText) ||
                orderedMessage.message.subject.toLowerCase().includes(searchText) ||
                orderedMessage.message.sender.displayName.toLowerCase().includes(searchText) ||
                orderedMessage.message.recipients?.find((r: IMessagingContact) => r.displayName.includes(searchText))
        );
    }
}

@Pipe({
    name: 'filterMessage',
})
export class FilterMessagePipe implements PipeTransform {
    public transform(messages: IMessage[], filter: any): any[] {
        if (!filter) {
            return [];
        }
        return messages.filter(
            (msg: IMessage) =>
                ((filter.informative && !msg.important) ||
                    (filter.important && msg.important) ||
                    (!filter.informative && !filter.important)) &&
                (!filter.levels?.length ||
                    (filter.levels?.length && !!msg.educationLevelId.find((id: number) => filter.levels?.includes(id))))
        );
    }
}

@Pipe({
    name: 'filterUser',
})
export class FilterUserPipe implements PipeTransform {
    public transform(users: IUser[], searchText: string): IUser[] {
        if (!users) {
            return [];
        }
        if (!searchText) {
            return users;
        }
        searchText = searchText.toLowerCase();

        return users.filter(
            (u: IUser) =>
                u.firstname.toLowerCase().indexOf(searchText) >= 0 ||
                u.lastname.toLowerCase().indexOf(searchText) >= 0 ||
                (u.firstname.toLowerCase() + ' ' + u.lastname.toLowerCase()).indexOf(searchText) >= 0 ||
                (u.lastname.toLowerCase() + ' ' + u.firstname.toLowerCase()).indexOf(searchText) >= 0
        );
    }
}

@Pipe({
    name: 'filterGarderies',
})
export class FilterGarderiePipe implements PipeTransform {
    public periodes = PerdiodsEnum;
    public transform(garderies: IPointing[], period: PerdiodsEnum): any[] {
        if (!garderies) {
            return [];
        }
        if (!period) {
            return garderies;
        }

        if (period === this.periodes.TODAY) {
            return garderies.filter((g: IPointing) => isToday(g.start));
        }
        if (period === this.periodes.WEEK) {
            return garderies.filter((g: IPointing) => isDateInThisWeekButNotToday(g.start));
        }
        if (period === this.periodes.MONTH) {
            return garderies.filter((g: IPointing) => isDateInThisMonth(g.start));
        }
        if (period === this.periodes.YEAR) {
            return garderies.filter((g: IPointing) => isDateInThisYear(g.start));
        }

        return garderies;
    }
}

@Pipe({
    name: 'orderPointingByDate',
})
export class OrderPointingByDatePipe implements PipeTransform {
    public transform(garderies: IPointing[]): IPointing[] {
        if (!garderies) {
            return [];
        }
        return [...garderies].sort((a: IPointing, b: IPointing) => a.start.getTime() - b.start.getTime());
    }
}

@Pipe({
    name: 'filterPointingByEducationLevel',
})
export class FilterPointingsByEducationLevel implements PipeTransform {
    public transform(garderies: IPointing[], educationLevelId: number): IPointing[] {
        if (!garderies.length) {
            return [];
        }
        if (educationLevelId === 0) {
            return garderies;
        }
        return garderies.filter((p: IPointing) => p.educationLevelId === educationLevelId);
    }
}

@Pipe({
    name: 'filterWalletTransactions',
})
export class FilterWalletTransactionPipe implements PipeTransform {
    public periodes = PerdiodsEnum;
    public transform(transactions: ITransactionV2[], period: PerdiodsEnum): any[] {
        if (!transactions) {
            return [];
        }
        if (!period) {
            return transactions;
        }

        if (period === this.periodes.TODAY) {
            return transactions.filter((g: ITransactionV2) => isToday(g.date));
        }
        if (period === this.periodes.WEEK) {
            return transactions.filter((g: ITransactionV2) => isDateInThisWeekButNotToday(g.date));
        }
        if (period === this.periodes.MONTH) {
            return transactions.filter((g: ITransactionV2) => isDateInThisMonth(g.date));
        }
        if (period === this.periodes.YEAR) {
            return transactions.filter((g: ITransactionV2) => isDateInThisYear(g.date));
        }

        return transactions;
    }
}

@Pipe({
    name: 'filterWalletTransactionsWithFilter',
})
export class FilterWalletTransactionWithFilterPipe implements PipeTransform {
    public periodes = PerdiodsEnum;
    public transform(transactions: ITransactionV2[], filters: IWalletFilter): any[] {
        if (!transactions) {
            return [];
        }
        if (!filters) {
            return transactions;
        }

        return transactions.filter((transaction: ITransactionV2) => {
            // filter start date
            if (filters.startDate && transaction.date.getTime() < filters.startDate?.getTime()) {
                return false;
            }
            // filter end date
            if (filters.endDate && transaction.date.getTime() > filters.endDate?.getTime()) {
                return false;
            }
            // filter search field
            if (
                filters.search &&
                !(
                    transaction.creator?.firstname?.toLowerCase().includes(filters.search.toLowerCase()) ||
                    transaction.creator?.lastname?.toLowerCase().includes(filters.search.toLowerCase()) ||
                    transaction.comment?.toLowerCase().includes(filters.search.toLowerCase()) ||
                    transaction.title?.toLowerCase().includes(filters.search.toLowerCase())
                )
            ) {
                return false;
            }
            // filter payment status
            if (filters.status?.length > 0 && !filters.status.includes(transaction.status)) {
                return false;
            }
            // filter debit type
            if (filters.debitType?.length > 0 && transaction.type === TransactionTypeEnumV2.DEBIT) {
                return (
                    (transaction.debit_type_id === 1 && filters.debitType.includes(DebitTypeEnumV2.PAYMENT)) ||
                    (transaction.debit_type_id === 2 && filters.debitType.includes(DebitTypeEnumV2.UNLOAD)) ||
                    (transaction.debit_type_id === 3 && filters.debitType.includes(DebitTypeEnumV2.UNLOAD_CLASS))
                );
            }
            // filter credit type
            if (filters.creditType?.length > 0 && transaction.type === TransactionTypeEnumV2.CREDIT) {
                if (transaction.credit_type_id !== 1 && transaction.credit_type_id !== 4 && transaction.credit_type_id !== 5) {
                    return false;
                }
                return (
                    (transaction.credit_type_id === 1 && filters.creditType.includes(CreditTypeEnumV2.CASH)) ||
                    (transaction.credit_type_id === 4 && filters.creditType.includes(CreditTypeEnumV2.REFOUND)) ||
                    (transaction.credit_type_id === 5 && filters.creditType.includes(CreditTypeEnumV2.PAYMENT))
                );
            }
            if (filters.debitType?.length > 0 && transaction.type === TransactionTypeEnumV2.CREDIT) return false;
            if (filters.creditType?.length > 0 && transaction.type === TransactionTypeEnumV2.DEBIT) return false;
            return true;
        });
    }
}

@Pipe({
    name: 'filterWalletTransactionsByDate',
})
export class FilterWalletTransactionByDatePipe implements PipeTransform {
    public transform(transactions: ITransactionV2[], dates: Date[]): any[] {
        const start: Date = dates[0];
        const end: Date = dates[1];
        if (!transactions) {
            return [];
        }
        if (!start || !end) {
            return transactions;
        }

        return transactions.filter((g: ITransactionV2) => g.date.getTime() >= start.getTime() && g.date.getTime() <= end.getTime());
    }
}
